import { updatePropertyFn } from "@repo/dynamicEditor/src/helpers/updatePropertiesHooks";
import { MS_IN_SECOND, PushErrorNotification } from "@repo/helpers";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo } from "react";

import { useOnboardingSessionsApi } from "../api/onboardingSessionsApi";
import * as Queries from "../api/queries";
import { useRepApi, useRepOnboardingQuery, useRepQueries } from "../api/repApi";
import { useSettingsApi } from "../api/settingsApi";
import { useRepContext } from "../contexts/editableItemContext";
import { RepSettings } from "../models/settings";

export function useSettingsQuery() {
	const { rep, repId } = useRepContext();
	const { getSettings, getRepSettings, getDynamicComponents } = useSettingsApi();
	const { branchId } = rep;

	const branchQuery = useQuery({
		queryKey: [Queries.SETTINGS, branchId],
		queryFn: loadBranchSettings,
		placeholderData: keepPreviousData,
	});
	const repQuery = useQuery({
		queryKey: [Queries.SETTINGS_REP, repId],
		queryFn: loadRepSettings,
		placeholderData: keepPreviousData,
	});
	const { mindFlashQuery } = useMindFlashSettingQuery();
	const deQuery = useQuery({
		queryKey: [Queries.SETTINGS_DE],
		queryFn: getDynamicComponents,
		placeholderData: keepPreviousData,
	});
	const branchSettings = branchQuery.data;
	const repSettings = repQuery.data;
	const mindFlashSetting = mindFlashQuery.data;
	const dynamicSections = deQuery.data;
	const loaded = branchQuery.isFetched && repQuery.isFetched && deQuery.isFetched;
	const isError = branchQuery.isError || repQuery.isError || deQuery.isError;
	const settings = useMemo(() => {
		if (!branchSettings || !repSettings) return null;
		return {
			...branchSettings,
			...repSettings,
			...mindFlashSetting,
			dynamicSections,
			backgroundCheckSettings: {
				...branchSettings.backgroundCheckSettings,
				...repSettings.backgroundCheckSettings,
			},
		};
	}, [branchSettings, repSettings, dynamicSections, mindFlashSetting]);

	useEffect(() => {
		if (isError) PushErrorNotification("Failed to load settings");
	}, [isError]);

	function loadBranchSettings() {
		return getSettings(branchId);
	}

	function loadRepSettings() {
		return getRepSettings(repId);
	}

	return [settings || defaultRepSettings, loaded, isError] as [RepSettings, boolean, boolean];
}

export function useMindFlashSettingQuery() {
	const { repId } = useRepContext();
	const { getMindflashSettings } = useSettingsApi();

	const mindFlashQuery = useQuery<any, string, any, (string | number)[]>({
		queryKey: [Queries.SETTINGS_MINDFLASH, repId],
		queryFn: loadMindFlashSettings,
		placeholderData: keepPreviousData,
	});
	const mindFlashError = mindFlashQuery.error && !mindFlashQuery.isLoading;
	return { mindFlashQuery, mindFlashError } as {
		mindFlashQuery: any;
		mindFlashError: boolean;
	};

	function loadMindFlashSettings() {
		return getMindflashSettings(repId);
	}
}

/** Runs update rep data, checking if Rep should be updated based on external data (like MF)
 * If updated, api returns true, and re-loads rep data.
 */
export function useUpdateRepData() {
	const { repId } = useRepContext();
	const { updateRepData } = useRepApi();
	const { checkOnboarding } = useRepOnboardingQuery();
	const { useRepInfoQuery } = useRepQueries();
	const { possibleStatesQuery, onboardingRequirementsQuery } = useOnboardingSessionsApi();
	const queryClient = useQueryClient();

	const repInfoQuery = useRepInfoQuery();

	const clearQueries = useCallback(async () => {
		await repInfoQuery.clearQuery();
		// TODO: should use queries
		await onboardingRequirementsQuery.clearQuery();
		await possibleStatesQuery.clearQuery();

		await queryClient.invalidateQueries({ queryKey: [Queries.SETTINGS_REP] });
	}, [repInfoQuery, onboardingRequirementsQuery, possibleStatesQuery, queryClient]);

	// React Query-managed throttling: manual query with 30s stale window
	const updateQuery = useQuery({
		queryKey: [Queries.UPDATE_REP_DATA, repId],
		queryFn: async () => {
			if (!repId) return false;
			const didUpdate = await updateRepData(repId);

			if (didUpdate) {
				await checkOnboarding();
				await clearQueries();
			}

			return didUpdate;
		},
		staleTime: 30 * MS_IN_SECOND,
	});

	return useCallback(async () => {
		if (!repId) return false;

		if (!updateQuery.isStale && updateQuery.data !== undefined) {
			return updateQuery.data;
		}

		if (updateQuery.isFetching) return;

		const res = await updateQuery.refetch();

		return res.data ?? false;
	}, [repId, updateQuery]);
}

export function useSegmentCascade<TData>(updateCallback: updatePropertyFn<TData>, parentPropName: string) {
	async function cascadeSegment(id: string, value: any, newState: TData) {
		async function update(name: string, value: any) {
			newState = await updateCallback(name, value, newState);
		}
		const clear = (name: string) => update(name, null);
		const clearOnParent = (name: string) => clear(`${parentPropName}.${name}`);
		//Custom logic
		switch (id) {
			case `${parentPropName}.channelId`:
				await clearOnParent(`marketerId`);
				await clearOnParent(`marketerName`);
				await clearOnParent(`brandId`);
				await clearOnParent(`brandName`);
				break;
			case `${parentPropName}.marketerId`:
				await clearOnParent(`stateId`);
				await clearOnParent(`stateName`);
				break;
			case `${parentPropName}.stateId`:
				await clearOnParent(`branchId`);
				await clearOnParent(`branchName`);
				break;
			case `${parentPropName}.branchId`:
				await clearOnParent(`managerId`);
				await clearOnParent(`managerName`);
				await clearOnParent(`trainerId`);
				await clearOnParent(`trainerName`);
				break;
		}
		return newState;
	}
	return useCallback(cascadeSegment, [updateCallback, parentPropName]);
}

const defaultRepSettings = {
	backgroundCheckSettings: { requiredChecks: {} as any },
	onboarding: true,
	tabSettings: {
		backgroundCheck: false,
		onboardingAudit: false,
		documentation: false,
		drugTest: false,
		fieldAudit: false,
		idValidation: false,
	},
	performanceAndStandardsName:
		"Performance and Ethical Standards document or Customer Care Performance Standards and Procedures",
	marketerDescription: "Vendor",
	legalEntityName: "",
	autogenerateLogin: false,
	dynamicSections: [],
} as RepSettings;
